import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { serverLocation } from 'src/app/globals';
import { WebsocketService } from 'src/app/services/websocket.service';
import { TokenService } from '../../shared/services/token.service';
import { ChangePasswordModel, User } from '../classes';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  get users(): Observable<User[]> {
    return combineLatest(Object.values(this._users));
  }
  private _users: { [key: number]: ReplaySubject<User> } = {};

  private _online = new BehaviorSubject<User[]>([]);
  online$ = this._online.asObservable();

  _allUsersLoaded = new BehaviorSubject(false);
  allUsersLoaded$ = this._allUsersLoaded.asObservable();

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService,
    private _websocketService: WebsocketService
  ) {
    this._websocketService.onlineUsers$
      .pipe(debounceTime(500), switchMap((value: string[]) => combineLatest(value.map(x => this.getUserById(x, true)))))
      .subscribe((user: User[]) => {
        this._online.next(user);
      });
  }

  changeUserPassword(model: ChangePasswordModel): Observable<Object> {
    return this._httpClient.post(`${serverLocation}/api/account/change-password`, model, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  getUserById(identifier: string, soft: boolean = true): Observable<User> {
    if (typeof identifier !== 'string') return throwError('no valid user identifier passed');
    if (!soft || !this._users[identifier]) {
      if (!this._users[identifier]) this._users[identifier] = new ReplaySubject(1);
      this._getUserById(identifier).subscribe({
        next: (user: User) => this._users[identifier].next(user),
        error: (error: HttpErrorResponse) => this._users[identifier].error(error)
      });
    }
    return this._users[identifier].asObservable();
  }

  loadAllAMSUsers(soft: boolean = true): Observable<User[]> {
    if (this._allUsersLoaded.value && soft) return combineLatest(Object.values(this._users));
    let subject = new Subject<User[]>();
    this._httpClient.get(`${serverLocation}/api/user/internal-users`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }).pipe(
      tap((result: User[]) => {
        for (let user of result) {
          if (!this._users[user.id]) this._users[user.id] = new ReplaySubject<User>();
          this._users[user.id].next(user);
        }
      }),
      switchMap(() => combineLatest(Object.values(this._users)))
    ).subscribe({
      next: (result) => subject.next(result),
      error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  loadAllUsersUsingWidgettype(widgetType: number): Observable<User[]> {
    return this._httpClient.get(`${serverLocation}/api/user/other-users-with-widgettype/${widgetType}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<User[]>;
  }

  updateUser(user: User): Observable<User> {
    return this._httpClient.put(`${serverLocation}/api/account`, user, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<User>;
  }

  uploadUserImage(formData: FormData): Observable<Object> {
    return this._httpClient.post(`${serverLocation}/api/account/upload-image`, formData, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  private _getUserById(identifier: string): Observable<User> {
    return this._httpClient.get(`${serverLocation}/api/user/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<User>;
  }


}
